import { SHOP_ITEMS, WALL, canBuy, type ShopItem } from "@engine";
import { DECO_BODY } from "../../art/design/village.ts";
import type { Session } from "../state.ts";
import { $, hide, show } from "./dom.ts";

const WALL_ART = `<path d="M6 18v-5h6v3h7v-3h6v3h6v-3h6v3h7v-3h6v5 24H6Z" fill="#cfc6b6" stroke="#2e2418" stroke-width="2.2" stroke-linejoin="round"/><path d="M6 26h44M6 34h44M18 18v8M32 26v8M22 34v8M40 18v8" stroke="#8a8277" stroke-width="1.6"/>`;

/** Original inline art for the repair cards (static markup, no user data). */
const REPAIR_ART: Record<string, string> = {
  "repair-walls": `${WALL_ART}<circle cx="44" cy="10" r="8" fill="#2f9e2a" stroke="#2e2418" stroke-width="2"/><path d="M44 6v8M40 10h8" stroke="#fff" stroke-width="2.4" stroke-linecap="round"/>`,
  "rebuild-walls": `<path d="M6 18v-5h6v3h7v-3h6v3h6v-3h6v3h7v-3h6v5 24H6Z" fill="#e6dfd2" stroke="#2e2418" stroke-width="2.2" stroke-linejoin="round"/><path d="M6 26h44M6 34h44M18 18v8M32 26v8M22 34v8M40 18v8" stroke="#a79f92" stroke-width="1.6"/><path d="M28 4l3 6 7 1-5 4 1 7-6-4-6 4 1-7-5-4 7-1Z" fill="#ffc928" stroke="#2e2418" stroke-width="1.8" stroke-linejoin="round"/>`,
};

function svg(markup: string, width: number, height: number): SVGSVGElement {
  const node = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  node.setAttribute("viewBox", `0 0 ${width} ${height}`);
  node.setAttribute("width", String(width));
  node.setAttribute("height", String(height));
  node.setAttribute("aria-hidden", "true");
  node.classList.add("art");
  node.innerHTML = markup;
  return node;
}

function price(item: ShopItem): string {
  if (item.goldCost && item.crystalCost) return `${item.goldCost} gold + ${item.crystalCost} crys`;
  if (item.crystalCost) return `${item.crystalCost} crystals`;
  return `${item.goldCost} gold`;
}

function shortPrice(item: ShopItem): string {
  return item.crystalCost && !item.goldCost ? `${item.crystalCost} crys` : `${item.goldCost} gold`;
}

function repairDetail(item: ShopItem, hp: number): string {
  if (item.restoreFull) return `Back to ${WALL.maxHp} HP`;
  const gain = item.wallHp ?? 0;
  return `+${gain} HP · ${hp} → ${Math.min(WALL.maxHp, hp + gain)}`;
}

function repairCard(item: ShopItem, session: Session, onBuy: (id: string) => void): HTMLElement {
  const state = session.result.state;
  const card = document.createElement("div");
  card.className = "repair-card";
  const copy = document.createElement("div");
  copy.className = "copy";
  const title = document.createElement("strong");
  title.textContent = item.name;
  const detail = document.createElement("span");
  detail.textContent = repairDetail(item, state.wallHp);
  copy.append(title, detail);
  const button = document.createElement("button");
  button.type = "button";
  button.className = `price-btn${item.crystalCost && !item.goldCost ? " crystal" : ""}`;
  button.textContent = shortPrice(item);
  const full = state.wallHp >= WALL.maxHp;
  button.disabled = !canBuy(state, item);
  button.setAttribute("aria-label", full ? `${item.name}: walls already full` : `${item.name} for ${price(item)}`);
  button.addEventListener("click", () => onBuy(item.id));
  card.append(svg(REPAIR_ART[item.id] ?? WALL_ART, 56, 48), copy, button);
  return card;
}

function decoCard(item: ShopItem, session: Session, onBuy: (id: string) => void): HTMLElement {
  const state = session.result.state;
  const owned = item.decorationId !== undefined && state.decorations.includes(item.decorationId);
  const button = document.createElement("button");
  button.type = "button";
  button.className = `deco-card${owned ? " owned" : ""}`;
  button.disabled = !owned && !canBuy(state, item);
  button.setAttribute("aria-label", owned ? `${item.name}: placed` : `Buy ${item.name} for ${price(item)}`);
  const name = document.createElement("span");
  name.className = "deco-name";
  name.textContent = item.name;
  const cost = document.createElement("span");
  cost.className = `deco-price${item.crystalCost && !item.goldCost ? " crystal" : ""}`;
  cost.textContent = owned ? "Placed" : price(item);
  const art = item.decorationId ? DECO_BODY[item.decorationId] : "";
  button.append(svg(art, 60, 54), name, cost);
  if (!owned) {
    button.addEventListener("click", () => onBuy(item.id));
  }
  return button;
}

export function renderShop(session: Session, onBuy: (itemId: string) => void): void {
  $("shop-gold").textContent = session.result.state.gold.toLocaleString("en-GB");
  $("shop-crystals").textContent = session.result.state.crystals.toLocaleString("en-GB");
  $("shop-repairs").replaceChildren(
    ...SHOP_ITEMS.filter((item) => item.kind === "repair").map((item) => repairCard(item, session, onBuy)),
  );
  $("shop-decos").replaceChildren(
    ...SHOP_ITEMS.filter((item) => item.kind === "decoration").map((item) => decoCard(item, session, onBuy)),
  );
  show($("shop-modal"));
}

export function bindShopTabs(): void {
  const scroller = $("shop-scroll");
  const tabs = [
    { button: $("tab-repairs"), target: $("shop-repairs-section") },
    { button: $("tab-decos"), target: $("shop-decos-section") },
  ];
  const activate = (index: number) =>
    tabs.forEach((tab, i) => {
      tab.button.classList.toggle("active", i === index);
      tab.button.setAttribute("aria-selected", String(i === index));
    });
  tabs.forEach((tab, index) =>
    tab.button.addEventListener("click", () => {
      activate(index);
      scroller.scrollTo({ top: tab.target.offsetTop - scroller.offsetTop - 4, behavior: "smooth" });
    }),
  );
  scroller.addEventListener("scroll", () => {
    const decosTop = tabs[1]?.target.offsetTop ?? 0;
    activate(scroller.scrollTop + scroller.offsetTop + 40 >= decosTop ? 1 : 0);
  });
}

export function closeShop(): void {
  hide($("shop-modal"));
}
